import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
export const RegisterStorePage = () => {
  const navigate = useNavigate();
  const { registerStore } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    org_number: '',
    address: '',
    contact_phone: '',
    customer_phone: '',
    acceptTerms: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Lösenorden matchar inte');
      return;
    }

    if (!formData.acceptTerms) {
      toast.error('Du måste godkänna användarvillkoren för butiker');
      return;
    }

    if (!formData.company_name.trim()) {
      toast.error('Butiksnamn är obligatoriskt');
      return;
    }
    if (!formData.org_number.trim()) {
      toast.error('Organisationsnummer är obligatoriskt');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Adress är obligatoriskt');
      return;
    }
    if (!formData.contact_phone.trim()) {
      toast.error('Kontaktpersonens telefonnummer är obligatoriskt');
      return;
    }
    if (!formData.customer_phone.trim()) {
      toast.error('Kundväxelnummer är obligatoriskt');
      return;
    }

    setLoading(true);

    try {
      const { message } = await registerStore(
        {
          email: formData.email,
          password: formData.password,
          name: formData.company_name,
          company_name: formData.company_name,
          accept_terms: true,
        },
        {
          name: formData.company_name,
          description: "",
          location: formData.address,
          county: "",
          org_number: formData.org_number,
          contact_email: formData.email,
          contact_phone: formData.contact_phone,
          customer_phone: formData.customer_phone,
          opening_hours: {}
        }
      );
      toast.success(message || 'Registrering mottagen. Verifiera din e-post och logga sedan in.');
      navigate('/logga-in');
    } catch (error) {
      console.error('Register error:', error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Kunde inte registrera. Kontrollera att alla fält är korrekt ifyllda.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center py-12 px-4" data-testid="register-store-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 mb-6">
            <span className="text-3xl font-black text-[#003B73]">Bygg</span>
            <span className="text-3xl font-black text-[#FF8A00]">Fynd</span>
          </Link>
          <h1 className="text-2xl font-black text-[#0F172A]">Registrera bygghandel</h1>
          <p className="text-gray-500 mt-2">
            Registrera din bygghandel för att börja sälja på ByggFynd.
          </p>
        </div>

        <div className="bg-white rounded-md shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company_name">Butiksnamn *</Label>
              <Input
                id="company_name"
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                className="mt-1"
                placeholder="T.ex. Byggvaruhuset AB"
                data-testid="register-company"
              />
            </div>

            <div>
              <Label htmlFor="org_number">Organisationsnummer *</Label>
              <Input
                id="org_number"
                type="text"
                value={formData.org_number}
                onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                required
                className="mt-1"
                placeholder="556xxx-xxxx"
                data-testid="register-org-number"
              />
            </div>

            <div>
              <Label htmlFor="address">Adress *</Label>
              <Input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className="mt-1"
                placeholder="Industrivägen 1, 123 45 Stockholm"
                data-testid="register-address"
              />
            </div>

            <div>
              <Label htmlFor="contact_phone">Kontaktpersonens nummer (för ByggFynd) *</Label>
              <Input
                id="contact_phone"
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                required
                className="mt-1"
                placeholder="070-xxx xx xx"
                data-testid="register-contact-phone"
              />
              <p className="text-xs text-gray-500 mt-1">Visas ej för kunder</p>
            </div>

            <div>
              <Label htmlFor="customer_phone">Kundväxelnummer (för kunder) *</Label>
              <Input
                id="customer_phone"
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
                className="mt-1"
                placeholder="08-xxx xx xx"
                data-testid="register-customer-phone"
              />
              <p className="text-xs text-gray-500 mt-1">Visas för kunder på din butikssida</p>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-[#0F172A] mb-3">Inloggningsuppgifter</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">E-postadress *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="info@foretag.se"
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Lösenord *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Bekräfta lösenord *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id="acceptTerms"
                checked={formData.acceptTerms}
                onCheckedChange={(checked) => setFormData({ ...formData, acceptTerms: Boolean(checked) })}
                data-testid="accept-terms"
              />
              <label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-tight">
                Jag har tagit del av och godkänner{' '}
                <Link to="/anvandarvillkor-butik" target="_blank" rel="noopener noreferrer" className="text-[#003B73] hover:underline font-medium">
                  användarvillkoren för butiker
                </Link>
                {' '}samt{' '}
                <Link to="/integritetspolicy" target="_blank" rel="noopener noreferrer" className="text-[#003B73] hover:underline font-medium">
                  integritetspolicyn
                </Link>
                .
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF8A00] hover:bg-[#e67c00] font-bold"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Registrerar...
                </>
              ) : (
                'Registrera bygghandel'
              )}
            </Button>

            <p className="text-xs text-center text-gray-500">
              Din bygghandel granskas inom 1-2 arbetsdagar innan den publiceras.
            </p>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500">
              Har du redan ett konto?{' '}
              <Link to="/logga-in" className="text-[#003B73] font-semibold hover:underline" data-testid="login-link">
                Logga in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterStorePage;
